// src/services/api.ts
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://localhost:7077',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use((config) => {
  // Add API key header (required by ApiKeyMiddleware)
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey;
  } else {
    console.error('API key not found in environment variables');
  }

  // Add JWT token for user authentication (if available)
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle specific error cases
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Check if it's an API key issue vs JWT token issue
          if (error.response.data?.error?.includes('API key')) {
            console.error('API key authentication failed');
            // Don't redirect to login for API key issues
          } else if (localStorage.getItem('homeToken')) {
            // A super admin working inside another company, whose acting token
            // has expired or been refused.
            //
            // Signing them out here would be wrong and it would be baffling: the
            // session they actually logged in with is still perfectly valid, and
            // the acting one was only ever meant to last an hour. Put the real
            // token back and reload, which lands them on the same page in their
            // own company.
            //
            // The reload rather than a redirect is deliberate - it re-runs the
            // AuthProvider mount, which is the one place that decides what the
            // stored keys mean, so this does not need to know.
            console.warn('Acting session ended - returning to your own company');
            const homeToken = localStorage.getItem('homeToken') as string;
            localStorage.setItem('token', homeToken);
            localStorage.removeItem('homeToken');
            localStorage.removeItem('actingAs');
            localStorage.removeItem('actingExpiresAt');
            window.location.reload();
          } else {
            // Handle unauthorized JWT - clear auth data and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
      }
    }
    return Promise.reject(error);
  }
);

export default api;